import React, { useState } from "react";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import { CRMColors } from "../../../models/CRMColors";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Strikethrough } from "../../BuildingBlocks/Strikethrough";
import { CRMRoadblockChecklistItemBase } from "../CRMRoadblockChecklistItemBase/CRMRoadblockChecklistItemBase";
import { Padding } from "../../BuildingBlocks/Padding";
import { Background } from "../../BuildingBlocks/Background";
import { PreserveLineBreaksNotSpaces } from "../../BuildingBlocks/PreserveLineBreaksNotSpaces";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { FontSize } from "../../BuildingBlocks/FontSize";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { TRoadblockEvidenceFact, TRoadblockEvidenceLink } from "../../../../../domain/codecs/form/RoadblockForm";
import { TChecklistItemResolvedByParty } from "../../../../../domain/codecs/ChecklistItemResolvedByParty";
import { CRMRoadblockPartyPill } from "../CRMRoadblockPartyPill/CRMRoadblockPartyPill";
import { CRMRoadblockEvidenceSummaryToggle, CRMRoadblockEvidenceFacts } from "../CRMRoadblockChecklistItemEvidence/CRMRoadblockChecklistItemEvidence";

type TRoadblockChecklistItemManualProps = {
    mode: "first" | "final";
    title: string;
    isDisabled: boolean;
    resolvedBy: string;
    resolvedByParty: TChecklistItemResolvedByParty;
    evidence: Array<TRoadblockEvidenceFact>;
    isCheckedFirst: boolean;
    isCheckedFinal: boolean;
    onChangeFinalCheck: (value: boolean) => void;
    onOpenEvidenceLink: (link: TRoadblockEvidenceLink) => void;
};

const ResolvedByToggle = (props: { isOpen: boolean; onToggle: () => void }): JSX.Element =>
    <div className="crm-tooltip__text" onClick={props.onToggle}>
        Resolved by&nbsp;
        <CRMIcon
            iconName={props.isOpen ? "arrow-up" : "tooltip-open"}
            size="tiny"
            colour="neutral-ink"
            inline={true}
        />
    </div>;

export const CRMRoadblockChecklistItemAutomatic = (props: React.PropsWithChildren<TRoadblockChecklistItemManualProps>): JSX.Element => {

    const [isEvidenceOpen, setIsEvidenceOpen] = useState(false);
    const [isInstructionOpen, setIsInstructionOpen] = useState(false);

    const IsFinalMode = props.mode === "final";
    const ShowInstruction = !props.isCheckedFirst || IsFinalMode;
    const HasEvidence = props.evidence.length > 0;

    const onChangeFinalCheck = () => {
        if (!props.isDisabled) {
            props.onChangeFinalCheck(!props.isCheckedFinal);
        }
    }

    return (
        <CRMRoadblockChecklistItemBase
            ParagraphElement={null}
            isDisabledFinalCheck={props.isDisabled}
            isDisabledFirstCheck={props.isDisabled}
            isShowingCheck={IsFinalMode && props.isCheckedFirst}
            isCheckedFinal={props.isCheckedFinal}
            onChangeFinalCheck={onChangeFinalCheck}
        >
            <SpacingColumn spacing={CRMSpacing.SMALL}>
                {/* TITLE + PARTY PILL */}
                <SpacingRow
                    spacing={CRMSpacing.TINY}
                    alignItems="flex-start"
                    childSize="1fr auto"
                >
                    <SpacingRow
                        spacing={CRMSpacing.TINY}
                        alignItems="flex-start"
                    >
                        <CRMIcon
                            iconName={props.isCheckedFirst ? "done" : "hourglass"}
                            colour="hex"
                            hexColour={CRMColors.NEUTRAL_0}
                        />

                        <Padding type="top" spacing={`2px`}>
                            <CRMParagraph>
                                <WeightSemiBold>
                                    {props.isCheckedFirst && !IsFinalMode &&
                                        <Strikethrough>{props.title}</Strikethrough>
                                    }
                                    {(!props.isCheckedFirst || IsFinalMode) &&
                                        <>{props.title}</>
                                    }
                                </WeightSemiBold>
                            </CRMParagraph>
                        </Padding>
                    </SpacingRow>

                    <CRMRoadblockPartyPill party={props.resolvedByParty} />
                </SpacingRow>

                {/* EVIDENCE SUMMARY + RESOLVED-BY TOGGLES */}
                {(HasEvidence || ShowInstruction) &&
                    <FontSize size={CRMFontSizes.SMALL} lineHeight={1.4}>
                        <SpacingRow childSize="1fr auto" alignItems="center">
                            {HasEvidence
                                ? <CRMRoadblockEvidenceSummaryToggle
                                    evidence={props.evidence}
                                    isOpen={isEvidenceOpen}
                                    onToggle={() => setIsEvidenceOpen((v) => !v)}
                                />
                                : <div />
                            }
                            {ShowInstruction
                                ? <ResolvedByToggle
                                    isOpen={isInstructionOpen}
                                    onToggle={() => setIsInstructionOpen((v) => !v)}
                                />
                                : <div />
                            }
                        </SpacingRow>
                    </FontSize>
                }

                {HasEvidence && isEvidenceOpen &&
                    <CRMRoadblockEvidenceFacts evidence={props.evidence} onOpenEvidenceLink={props.onOpenEvidenceLink} />
                }

                {ShowInstruction && isInstructionOpen &&
                    <Background padding="8px" color={CRMColors.HIGHLIGHTS_CALMING_PINK_12} borderRadius="5px">
                        <CRMParagraph>
                            <PreserveLineBreaksNotSpaces>{props.resolvedBy}</PreserveLineBreaksNotSpaces>
                        </CRMParagraph>
                    </Background>
                }
            </SpacingColumn>
        </CRMRoadblockChecklistItemBase>
    );
};
